import { FormEvent, useState } from 'react'
import { uploadAssignmentFile } from '@/lib/assignments'
import { createSubmission, type AssignmentSubmission } from '@/lib/submissions'

interface StudentSubmitFormProps {
  assignmentId: string
  courseId: string
  courseSlug: string
  moduleId: string
  userId: string
  studentName: string
  studentEmail: string
  onCreated: (record: AssignmentSubmission) => void
}

function StudentSubmitForm({
  assignmentId,
  courseId,
  courseSlug,
  moduleId,
  userId,
  studentName,
  studentEmail,
  onCreated,
}: StudentSubmitFormProps) {
  const [note, setNote] = useState('')
  const [fileUrl, setFileUrl] = useState('')
  const [fileName, setFileName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const onUpload = async (file?: File) => {
    if (!file) return
    setError('')
    setBusy(true)
    try {
      const uploaded = await uploadAssignmentFile(file)
      setFileUrl(uploaded.url)
      setFileName(uploaded.name)
    } catch (err) {
      console.error('No se pudo subir la entrega:', err)
      setError(err instanceof Error ? err.message : 'No se pudo subir el archivo.')
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!fileUrl && !note.trim()) {
      setError('Añade un archivo o una nota de entrega.')
      return
    }
    setError('')
    setBusy(true)
    try {
      const record = await createSubmission({
        assignmentId,
        courseId,
        courseSlug,
        moduleId,
        userId,
        studentName,
        studentEmail,
        note,
        fileUrl,
        fileName,
      })
      onCreated(record)
    } catch (err) {
      console.error('No se pudo enviar la entrega:', err)
      setError('No se pudo enviar. Inténtalo de nuevo.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-3 border border-dashed border-[#1E3A5F]/15 p-4">
      <p className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">Tu entrega</p>
      <label className="block text-xs">
        Nota
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs">
        Archivo
        <input
          type="file"
          onChange={(event) => void onUpload(event.target.files?.[0])}
          className="mt-1 block w-full text-sm file:mr-3 file:border file:border-[#1E3A5F]/20 file:bg-transparent file:px-3 file:py-1.5"
        />
      </label>
      {fileName ? <p className="text-xs text-[#1E3A5F]/60">Adjunto: {fileName}</p> : null}
      {error ? <p className="text-xs text-[#C41E3A]">{error}</p> : null}
      <button type="submit" disabled={busy} className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6] disabled:opacity-50">
        {busy ? 'Enviando…' : 'Entregar'}
      </button>
    </form>
  )
}

export default StudentSubmitForm
